import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Refresh01Icon } from '@hugeicons/core-free-icons';
import { useLocation, Navigate } from 'react-router-dom';
import { useAppSession } from '../hooks/useAppSession';
import { AUTH_CALLBACK_PATH, useAuth, useClerk, useSignIn } from '../lib/auth';
import { authApi } from '../lib/api';
import { Logo } from '../components/ui/Logo';
import { log } from '@/lib/logger';
import { capture } from '../lib/analytics';
import { toUserFacingMessage } from '@overlayqa/shared';

/**
 * Extension auth pass-through page.
 *
 * This page is opened by the extension in a background tab. The flow:
 *
 * 1. Tab opens → immediately redirects to Google OAuth
 * 2. Google authenticates → redirects back here
 * 3. Handoff completes → popup closes itself
 *
 * Onboarding wizard (with pin extension step) is opened separately by
 * authPolling in the content script for new users.
 */
export function ExtensionAuthPage() {
  const location = useLocation();
  const { getToken, isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const { isLoaded: isSignInLoaded, signIn } = useSignIn();
  const { client } = useClerk();
  const { data: session } = useAppSession();
  const [completionError, setCompletionError] = useState<string | null>(null);
  const googleRedirectAttempted = useRef(false);
  const handoffAttempted = useRef(false);

  const currentUrl = `${location.pathname}${location.search}`;
  const handoffCode = new URLSearchParams(location.search).get('code');

  // Catch Clerk's internal "already signed in" rejection — fires on an internal
  // promise chain that doesn't connect to the promise returned by signIn methods.
  // Instead of redirecting to the server finish page (wrong origin — Clerk sessions
  // are origin-scoped), we suppress the error and let Clerk hydrate on this domain.
  // Step 2 will fire once isSignedIn becomes true.
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      const msg = event.reason?.message || String(event.reason || '');
      log.info('[ExtensionAuth] unhandledrejection:', { msg });
      if (msg.toLowerCase().includes('already signed in') && handoffCode) {
        event.preventDefault();
        googleRedirectAttempted.current = true;
        log.info('[ExtensionAuth] "Already signed in" caught — waiting for Clerk to hydrate on dashboard domain');
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, [handoffCode]);

  // Step 1: Auto-redirect to Google OAuth when unauthenticated
  useEffect(() => {
    log.info('[ExtensionAuth] Step 1 firing:', { isAuthLoaded, isSignedIn, isSignInLoaded, handoffCode });
    if (!isAuthLoaded) return; // Wait for Clerk auth state to resolve

    // Clerk may have activeSessions before isSignedIn hydrates — skip OAuth
    // and let Step 2 fire once isSignedIn becomes true
    const hasActiveSession = isSignedIn
      || (client?.activeSessions && client.activeSessions.length > 0);

    log.info('[ExtensionAuth] hasActiveSession:', { hasActiveSession, activeSessions: client?.activeSessions?.length });

    if (hasActiveSession || !handoffCode || googleRedirectAttempted.current) return;
    if (!isSignInLoaded || !signIn) return;

    googleRedirectAttempted.current = true;
    log.info('[ExtensionAuth] Auto-redirecting to Google OAuth');

    signIn.authenticateWithRedirect({
      redirectUrl: AUTH_CALLBACK_PATH,
      redirectUrlComplete: currentUrl,
      strategy: 'oauth_google',
    }).catch((err) => {
      log.error('[ExtensionAuth] Google redirect .catch():', { message: err?.message || err });
      if (err?.message?.toLowerCase().includes('already signed in') && handoffCode) {
        // Don't redirect to server finish page — Clerk sessions are origin-scoped.
        // Keep googleRedirectAttempted=true so we don't retry OAuth, and let
        // Clerk hydrate isSignedIn on this domain so Step 2 can complete handoff.
        log.info('[ExtensionAuth] "Already signed in" in .catch — waiting for Clerk hydration');
        return;
      }
      googleRedirectAttempted.current = false;
    });
  }, [isAuthLoaded, isSignedIn, isSignInLoaded, signIn, handoffCode, currentUrl, client]);

  // Timeout fallback: if nothing completes after 20s, show an error so the user
  // isn't stuck on a blank tab forever.
  useEffect(() => {
    if (!handoffCode) return;
    const timer = setTimeout(() => {
      if (!handoffAttempted.current) {
        log.error('[ExtensionAuth] 20s timeout — handoff never completed');
        capture('extension_auth_handoff_failed', { reason: 'page_timeout_20s' });
        setCompletionError('Sign-in timed out. Please close this tab and try again from the extension.');
      }
    }, 20000);
    return () => clearTimeout(timer);
  }, [handoffCode]);

  // Step 2: Complete handoff after Google auth returns, then close popup.
  // Onboarding wizard (opened by authPolling in content script) handles
  // setup + pin extension instructions for new users.
  useEffect(() => {
    if (!handoffCode || !session || handoffAttempted.current) return;
    if (!isSignedIn) return;

    handoffAttempted.current = true;

    const completeHandoff = async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');

        log.info('[ExtensionAuth] Completing handoff');
        await authApi.completeExtensionHandoff(token, {
          code: handoffCode,
          teamId: session.activeTeamId,
        });
        capture('extension_auth_handoff_completed', {});

        log.info('[ExtensionAuth] Handoff complete — closing auth popup');
        window.close();
      } catch (error) {
        log.error('[ExtensionAuth] Handoff failed:', { error });
        capture('extension_auth_handoff_failed', {
          reason: error instanceof Error ? error.message.slice(0, 200) : 'unknown',
        });
        setCompletionError(
          toUserFacingMessage(error, 'Failed to complete extension sign-in')
        );
      }
    };

    void completeHandoff();
  }, [getToken, handoffCode, isSignedIn, session]);

  // No handoff code — user navigated here directly (stale bookmark, manual URL).
  // Redirect to sign-in instead of showing a blank page.
  if (!handoffCode && isAuthLoaded) {
    log.info('[ExtensionAuth] No handoff code — redirecting to /sign-in');
    return <Navigate to="/sign-in" replace />;
  }

  // Error state — sign-in or handoff failed
  if (completionError) {
    return (
      <div className="auth-shell min-h-screen px-6 py-10 sm:px-8">
        <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center">
          <div className="auth-shell__panel w-full max-w-sm">
            <div className="space-y-6 text-center">
              <Logo showText size="lg" className="justify-center" />
              <div className="space-y-3">
                <h2 className="hero-title">Sign-in failed</h2>
                <div className="auth-card__form-error">{completionError}</div>
                <p className="text-sm text-gray-500">
                  Close this tab and try again from the extension.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state — waiting for OAuth redirect or handoff completion
  return (
    <div className="auth-shell min-h-screen px-6 py-10 sm:px-8">
      <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center">
        <div className="space-y-6 text-center">
          <Logo showText size="lg" className="justify-center" />
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <HugeiconsIcon icon={Refresh01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4 animate-spin" />
            Signing you in...
          </div>
        </div>
      </div>
    </div>
  );
}
